import {
  getAuthoredTemplate,
  shouldPublishAuthoredMethodology,
  type AuthoredNarrativeTemplate,
} from './authoredNarrativeLoader';
import {
  resolveEquityV2,
  resolveEquityV6Compact,
  type EquityResolveContext,
  type EquityV6Compact,
} from './equityV2Resolver';
import { resolveRelevanceV2, type RelevanceV2Compact } from './relevanceV2Resolver';
import { renderNarrativeTemplate, type NarrativeTokenMap } from './narrativeTemplateEngine';

export type AuthoredNarrativeBundle = {
  overview: string;
  keyInsights: string;
  relevance: string;
  /** When present, public Relevance tab renders compact authored structure (intro + cards). */
  relevanceV2?: RelevanceV2Compact | null;
  narrativeChartId?: string;
  narrativeViewId?: string;
  /** When present, public Equity tab renders compact authored structure (intro + cards). */
  equityV6?: EquityV6Compact | null;
  equity: string;
  methodology?: {
    primarySources: string[];
    calculation: string[];
    limitations: string[];
    technicalDetails: string[];
  };
  authoringStatus: string;
  usedAuthoredCopy: boolean;
};

function splitLines(s: string): string[] {
  return s
    .split(/\n+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

export function renderAuthoredNarrativeBundle(
  chartId: string,
  viewId: string,
  tokens: NarrativeTokenMap,
  fallback?: Partial<AuthoredNarrativeBundle>,
  equityContext?: EquityResolveContext
): AuthoredNarrativeBundle {
  const spec = getAuthoredTemplate(chartId, viewId);
  if (!spec) {
    return {
      overview: fallback?.overview ?? '',
      keyInsights: fallback?.keyInsights ?? '',
      relevance: fallback?.relevance ?? '',
      equity: fallback?.equity ?? '',
      authoringStatus: 'MISSING_SPEC',
      usedAuthoredCopy: false,
      methodology: fallback?.methodology,
    };
  }

  const keyInsights = renderNarrativeTemplate(spec.key_insights_template, tokens);
  const templateEquity = spec.equity?.trim() ?? '';
  const equityV6 = resolveEquityV6Compact(chartId, viewId, equityContext);
  const equityV2 = equityV6 ? resolveEquityV2(chartId, viewId, equityContext) : undefined;
  const relevanceV2 = resolveRelevanceV2(chartId, viewId);
  const bundle: AuthoredNarrativeBundle = {
    overview: spec.overview?.trim() ?? '',
    keyInsights: keyInsights || (fallback?.keyInsights ?? ''),
    relevance: spec.relevance?.trim() ?? '',
    relevanceV2,
    equityV6,
    narrativeChartId: chartId,
    narrativeViewId: viewId,
    equity: equityV2 ?? templateEquity ?? (fallback?.equity ?? ''),
    authoringStatus: spec.authoring_status,
    usedAuthoredCopy: true,
  };

  if (shouldPublishAuthoredMethodology(spec.authoring_status) && spec.methodology) {
    const m = spec.methodology;
    bundle.methodology = {
      primarySources: splitLines(m.sources),
      calculation: splitLines(m.calculation),
      limitations: splitLines(m.limitations),
      technicalDetails: [
        ...splitLines(m.technical),
        ...(m.source_urls ? [`Source URLs: ${m.source_urls}`] : []),
      ],
    };
  } else if (fallback?.methodology) {
    bundle.methodology = fallback.methodology;
  }

  return bundle;
}

export function getAuthoredOverview(chartId: string, viewId: string, fallback: string): string {
  const spec = getAuthoredTemplate(chartId, viewId);
  return spec?.overview?.trim() || fallback;
}
